import {
  collection,
  onSnapshot,
  query,
  Unsubscribe,
  where
} from "firebase/firestore";
import { useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { auth, db } from "../api/firebaseApi";
import {
  addNewDocument,
  clearAllDocuments,
  deleteDocument,
  setAllDocuments,
  setIsLoadingDocuments,
  updateDocuments
} from "../redux/documents/documentSlice";
import { VisaDocumentType } from "../redux/documents/types";
import { store, useTypedDispatch } from "../redux/store";
import { DataDocs } from "../types/tables-data";

/**
 * Keeps the Redux document cache in sync with the case's Firestore document
 * collections. Ordering and grouping are the case filesystem's job now, so this
 * only mirrors the records as a flat list.
 */

type DocumentPath = {
  path: string;
  documentType: VisaDocumentType;
};

/**
 * The document fields other surfaces actually read. A Firestore write that
 * touches nothing here (a processing heartbeat, say) is dropped rather than
 * dispatched, so a burst of OCR updates doesn't re-render every consumer.
 */
const getDocumentSignature = (document: DataDocs | undefined) => {
  if (!document) return "";
  return JSON.stringify({
    id: document.id ?? "",
    type: document.type ?? "",
    customType: document.customType ?? "",
    docNames: document.docNames ?? "",
    autoTitle: document.autoTitle ?? "",
    autoTitleStatus: document.autoTitleStatus ?? "",
    exhibit: document.exhibit ?? "",
    isNew: Boolean(document.isNew),
    hasSummary: Boolean(document.summary),
    hasLastUpdatedAt: Boolean(document.last_updated_at),
    isDeleted: Boolean(document.isDeleted),
    statusStatus: document.status?.status ?? null
  });
};

const toDataDocs = (
  snapshotDoc: {
    id: string;
    ref: { path: string };
    data: () => Record<string, unknown>;
  },
  path: DocumentPath
): DataDocs =>
  ({
    ...snapshotDoc.data(),
    id: snapshotDoc.id,
    docRef: snapshotDoc.ref.path,
    isDeleted: snapshotDoc.data().isDeleted ?? false,
    documentType: path.documentType,
    collectionPath: path.path
  } as DataDocs);

const useDocuments = (paths: DocumentPath[], refetch?: boolean) => {
  const { id } = useParams();
  const dispatch = useTypedDispatch();

  // Firestore delivers each collection's changes separately; batching them into
  // one dispatch per frame keeps a bulk upload from thrashing subscribers.
  const pendingUpdatesRef = useRef<Map<string, DataDocs>>(new Map());
  const pendingFrameRef = useRef<number | null>(null);

  const flushPendingUpdates = useCallback(() => {
    pendingFrameRef.current = null;
    const documentsToUpdate = Array.from(pendingUpdatesRef.current.values());
    pendingUpdatesRef.current.clear();
    if (documentsToUpdate.length === 0) return;
    dispatch(updateDocuments(documentsToUpdate));
  }, [dispatch]);

  const queueUpdate = useCallback(
    (document: DataDocs) => {
      if (!document.id) return;
      pendingUpdatesRef.current.set(document.id, document);
      if (pendingFrameRef.current !== null) return;
      pendingFrameRef.current =
        window.requestAnimationFrame(flushPendingUpdates);
    },
    [flushPendingUpdates]
  );

  // Clear the previous case's documents on navigation so a stale list never
  // shows under a new case while its queries are still in flight.
  useEffect(() => {
    if (refetch !== undefined) return;
    dispatch(clearAllDocuments());
    if (id) dispatch(setIsLoadingDocuments(true));
  }, [id, dispatch, refetch]);

  useEffect(() => {
    let isCancelled = false;
    const activeUnsubscribes: Unsubscribe[] = [];

    const listen = () => {
      dispatch(setIsLoadingDocuments(true));
      const seeded = new Set<string>();
      let seededPaths = 0;

      paths.forEach((path) => {
        const filteredQuery = query(
          collection(db, path.path),
          where("isDeleted", "==", false)
        );

        const unsubscribe = onSnapshot(filteredQuery, (snapshot) => {
          if (isCancelled) return;

          // The first snapshot per collection is the initial read; seed it in
          // one write rather than as a stream of "added" changes.
          if (!seeded.has(path.path)) {
            seeded.add(path.path);
            const initial = snapshot.docs.map((snapshotDoc) =>
              toDataDocs(snapshotDoc, path)
            );
            const existing = store
              .getState()
              .document.allDocuments.filter(
                (document) => document.collectionPath !== path.path
              );
            dispatch(setAllDocuments([...existing, ...initial]));
            seededPaths += 1;
            if (seededPaths === paths.length) {
              dispatch(setIsLoadingDocuments(false));
            }
            return;
          }

          snapshot.docChanges().forEach((change) => {
            const document = toDataDocs(change.doc, path);

            if (change.type === "removed" || document.isDeleted === true) {
              dispatch(deleteDocument(document));
              return;
            }
            if (change.type === "added") {
              dispatch(addNewDocument(document));
              return;
            }
            if (document.isProcessing) return;

            const previous = store
              .getState()
              .document.allDocuments.find(
                (candidate) => candidate.id === document.id
              );
            if (
              getDocumentSignature(previous) === getDocumentSignature(document)
            ) {
              return;
            }
            queueUpdate(document);
          });
        });

        if (isCancelled) {
          unsubscribe();
          return;
        }
        activeUnsubscribes.push(unsubscribe);
      });
    };

    const unsubAuth = auth.onAuthStateChanged((user) => {
      if (id && user && refetch === undefined) listen();
    });

    return () => {
      isCancelled = true;
      unsubAuth();
      activeUnsubscribes.forEach((unsubscribe) => unsubscribe());
      if (pendingFrameRef.current !== null) {
        window.cancelAnimationFrame(pendingFrameRef.current);
        pendingFrameRef.current = null;
        pendingUpdatesRef.current.clear();
      }
    };
  }, [id, refetch, dispatch, queueUpdate]);

  return { uid: id };
};

export default useDocuments;
